import { useMemo } from "react";
import { createSearchParams, useNavigate } from "react-router-dom";
import classNames from "classnames";
import { makeLangUrl } from "helpers/makeLangUrl";

// Components
import Button from "components/atoms/Button";
import Progressbar from "components/atoms/Progressbar";

// Constants
import { CATEGORIES_ENUMS } from "constants/navigation/categories";
import { GET_PARAMS, GET_POPUP_TYPES } from "constants/popupConfig";

// Hooks
import { useGTM } from "hooks/useGTM";
import useTranslate from "hooks/useTranslate";
import { useCheckBalance } from "hooks/useCheckBalance";

// Types
import {
  BONUS_STATUS,
  BONUS_TYPES,
  BonusListType,
  FREE_SPINS_STAGES,
} from "types/Bonus";

// Url
import url from "url/index";
import BonusCardInfo from "../BonusCardInfo";

import "./styles.sass";

type BonusCardFaceProps = {
  bonus: BonusListType;
  setCanceledBonus?: (bonus: BonusListType) => void;
  clickInfo: () => void;
  currency: string;
};

const BonusCardFace = (props: BonusCardFaceProps) => {
  const { bonus, setCanceledBonus, clickInfo, currency } = props;

  const t = useTranslate();
  const navigate = useNavigate();
  const checkBalance = useCheckBalance();
  const GTM = useGTM();
  const isFreeSpins =
    "nameGameFreeSpin" in bonus && bonus.type === BONUS_TYPES.FREE_SPINS;

  const handleActive = () => {};

  const handleDeposit = () => {
    navigate({
      search: `?${createSearchParams({ [GET_PARAMS.POPUP]: GET_POPUP_TYPES.popup.deposit })}`,
    });
  };

  const handleCancel = () => {
    navigate({
      search: `?${createSearchParams({ [GET_PARAMS.POPUP]: GET_POPUP_TYPES.popup.cancelBonus })}`,
    });
    if (setCanceledBonus) {
      // eslint-disable-next-line react/destructuring-assignment
      setCanceledBonus(props.bonus);
    }
  };

  const handlePlay = () => {
    GTM.clickBonusAccept();
    if (
      bonus.type === BONUS_TYPES.REWARD ||
      bonus.type === BONUS_TYPES.NOMINAL ||
      bonus.type === BONUS_TYPES.TRIGGER
    ) {
      navigate(
        makeLangUrl(url.routes.casino.category([CATEGORIES_ENUMS.SLOTS])),
      );
    }
    if (isFreeSpins && bonus.freeSpinStage === FREE_SPINS_STAGES.UNUSED) {
      navigate({
        pathname: makeLangUrl(
          url.routes.casino.game({
            game: bonus.gameUrlAlias?.replaceAll(" ", "-")?.toLowerCase(),
          }),
        ),
      });
    }
    if (isFreeSpins && bonus.freeSpinStage === FREE_SPINS_STAGES.USED) {
      navigate(
        makeLangUrl(url.routes.casino.category([CATEGORIES_ENUMS.SLOTS])),
      );
    }
    // Не удалять, потенциально может пригодиться !
    // if (
    //   isFreeSpins &&
    //   bonus.freeSpinStage === FREE_SPINS_STAGES.USED &&
    //   bonus.wagerGameIds.length
    // ) {
    //   navigate({
    //     pathname: makeLangUrl(
    //       url.routes.casino.game({ uuid: bonus.wagerGameIds[0] }),
    //     ),
    //   });
    // }
    // if (
    //   isFreeSpins &&
    //   bonus.freeSpinStage === FREE_SPINS_STAGES.USED &&
    //   bonus.wagerProductNames.length
    // ) {
    //   navigate(
    //     makeLangUrl(url.routes.casino.category([bonus.wagerProductNames[0]])),
    //   );
    // }
    // if (
    //   isFreeSpins &&
    //   bonus.freeSpinStage === FREE_SPINS_STAGES.USED &&
    //   bonus.wagerProviderNames.length
    // ) {
    //   navigate(
    //     makeLangUrl(url.routes.casino.providers([bonus.wagerProviderNames[0]])),
    //   );
    // }
    if (bonus.type === BONUS_TYPES.SPINS && "gameUrlAlias" in bonus) {
      const isBalancePositive = checkBalance();

      if (isBalancePositive) {
        navigate({
          pathname: makeLangUrl(
            url.routes.casino.game({ game: bonus.gameUrlAlias }),
          ),
        });
      }
    }
  };

  const headerClass = classNames("BonusCardFace-header", {
    active: "progress" in bonus,
  });

  const title = useMemo(() => {
    if ("title" in bonus) {
      return t(bonus.title);
    }
    if ("name" in bonus) {
      return t(bonus.name);
    }
    return "";
  }, [bonus, t]);

  return (
    <div className="BonusCardFace">
      <div className={headerClass}>
        {title}
        {(bonus.status.toLowerCase() === BONUS_STATUS.ACTIVE ||
          bonus.status.toLowerCase() === BONUS_STATUS.AVAILABLE ||
          bonus.status.toLowerCase() === BONUS_STATUS.AVAILABLE_QUEUE ||
          bonus.type === BONUS_TYPES.FREE_SPINS ||
          bonus.type === BONUS_TYPES.NOMINAL) && (
          // eslint-disable-next-line react/button-has-type
          <button
            className="BonusCardFace-header__description"
            // eslint-disable-next-line react/jsx-handler-names
            onClick={clickInfo}
          >
            info
          </button>
        )}
      </div>
      <div className="BonusCardFace-content">
        <BonusCardInfo bonus={bonus} currency={currency} />
        {bonus.status.toLowerCase() === BONUS_STATUS.ACTIVE &&
        "progress" in bonus ? (
          <div className="BonusCardFace-content-progress">
            <div className="BonusCardFace-content--progress_bar">
              <Progressbar width={bonus.progress} />
            </div>
          </div>
        ) : null}
        <div className="BonusCardFace-content__controls">
          {bonus.status.toLowerCase() === BONUS_STATUS.ACTIVE && (
            <>
              <Button
                label="bonus.block.discard"
                variation="transparent"
                onClick={handleCancel}
              />
              <Button
                label="bonus.block.play"
                variation="primary"
                onClick={handlePlay}
              />
            </>
          )}
          {bonus.status.toLowerCase() === BONUS_STATUS.QUEUE && (
            <div className="BonusCardFace__queue">{t("bonus.queue.text")}</div>
          )}
          {(bonus.status.toLowerCase() === BONUS_STATUS.AVAILABLE ||
            bonus.status.toLowerCase() === BONUS_STATUS.AVAILABLE_QUEUE) && (
            <Button
              label="menu.left.deposit"
              variation="primary"
              onClick={handleDeposit}
            />
          )}
          {bonus.status.toLowerCase() === BONUS_STATUS.DEACTIVE && (
            <Button
              label="bonus.block.active"
              variation="primary"
              onClick={handleActive}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default BonusCardFace;
